import { DifficultyId } from '../constants';

export type StageGrade = 'S' | 'A' | 'B' | 'C';

export interface StageRankingResult {
  readonly stageScore: number;
  readonly grade: StageGrade;
  /** 1..5 when this run entered the board, 0 when it did not. */
  readonly placement: number;
  readonly isNewBest: boolean;
  readonly previousBest: number;
  readonly topScores: readonly number[];
}

interface StoredBoards {
  readonly version: 1;
  readonly boards: Record<string, readonly number[]>;
}

const STORAGE_KEY = 'lumina-archive.stage-rankings.v1';
const BOARD_SIZE = 5;
const S_SCORE_BY_STAGE = new Uint32Array([44_000, 95_000, 90_000, 250_000]);
const DIFFICULTY_SCORE_SCALE = new Float32Array([.78, 1, 1.28]);
const A_RATIO = .72;
const B_RATIO = .48;

function boardKey(difficulty: DifficultyId, stageId: number): string {
  return `${difficulty}:${stageId}`;
}

function stageGrade(score: number, difficulty: DifficultyId, stageId: number): StageGrade {
  const base = S_SCORE_BY_STAGE[Math.max(0, Math.min(S_SCORE_BY_STAGE.length - 1, stageId | 0))]
    ?? S_SCORE_BY_STAGE[0]!;
  const difficultyScale = DIFFICULTY_SCORE_SCALE[difficulty] ?? 1;
  const sScore = Math.round(base * difficultyScale);
  if (score >= sScore) return 'S';
  if (score >= Math.round(sScore * A_RATIO)) return 'A';
  if (score >= Math.round(sScore * B_RATIO)) return 'B';
  return 'C';
}

function sanitizeBoards(value: unknown): Record<string, readonly number[]> {
  if (!value || typeof value !== 'object') return {};
  const candidate = value as Partial<StoredBoards>;
  if (candidate.version !== 1 || !candidate.boards || typeof candidate.boards !== 'object') return {};
  const sanitized: Record<string, readonly number[]> = {};
  for (const [key, scores] of Object.entries(candidate.boards)) {
    if (!Array.isArray(scores)) continue;
    sanitized[key] = Object.freeze(
      scores
        .filter((score): score is number => typeof score === 'number' && Number.isFinite(score) && score >= 0)
        .map((score) => Math.round(score))
        .sort((a, b) => b - a)
        .slice(0, BOARD_SIZE),
    );
  }
  return sanitized;
}

/** R2専用の端末内ランキング。storage拒否時も現在セッションの順位は維持する。 */
export class StageRankingStore {
  private boards: Record<string, readonly number[]> = this.load();

  record(difficulty: DifficultyId, stageId: number, rawScore: number): StageRankingResult {
    const score = Math.max(0, Math.round(rawScore));
    const key = boardKey(difficulty, stageId);
    const previous = this.boards[key] ?? [];
    const previousBest = previous[0] ?? 0;
    const placement = Math.min(BOARD_SIZE + 1, 1 + previous.filter((value) => value > score).length);
    const topScores = Object.freeze([...previous, score].sort((a, b) => b - a).slice(0, BOARD_SIZE));
    this.boards = { ...this.boards, [key]: topScores };
    this.save();
    return Object.freeze({
      stageScore: score,
      grade: stageGrade(score, difficulty, stageId),
      placement: placement <= BOARD_SIZE ? placement : 0,
      isNewBest: previousBest === 0 || score > previousBest,
      previousBest,
      topScores,
    });
  }

  private load(): Record<string, readonly number[]> {
    try {
      if (typeof localStorage === 'undefined') return {};
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? sanitizeBoards(JSON.parse(raw) as unknown) : {};
    } catch {
      return {};
    }
  }

  private save(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, boards: this.boards }));
    } catch {
      // Private browsing/storage denial: the in-memory board remains authoritative.
    }
  }
}
